import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  Platform,
  Image,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useAppStore } from '../store/appStore';
import { useAppContext } from '../contexts/AppContext';
import { MoreOptionsModal } from './MoreOptionsModal';
import { colors, spacing, typography } from '../theme';
import { useLocale } from '../contexts/LocaleContext';
import { useTypography } from '../hooks/useTypography';
import { useRTLStyles } from '../hooks/useRTLStyles';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useThemedStyles } from '../hooks/useThemedStyles';

interface TabItem {
  id: string;
  icon: string;
  label: string;
  screen: string;
}

interface BottomTabsProps {
  onTabPress: (tabId: string) => void;
}

export const BottomTabs: React.FC<BottomTabsProps> = ({
  onTabPress,
}) => {
  const styles = useThemedStyles(getStyles);
  const { user } = useAppStore();
  const { activeTab } = useAppContext();
  const { t } = useLocale();
  const typo = useTypography();
  const rtl = useRTLStyles();
  const [showMoreModal, setShowMoreModal] = useState(false);
  const [imageError, setImageError] = useState(false);
  const insets = useSafeAreaInsets();
  
  // Reset image error when profile picture URL changes
  const profilePictureUrl = user?.avatar || user?.profilePicture;
  useEffect(() => {
    setImageError(false);
  }, [profilePictureUrl]);
  const tabs: TabItem[] = [
    { id: 'home',     icon: 'home-outline',          label: t('tab.home'),     screen: 'home' },
    { id: 'contacts', icon: 'person-outline',         label: t('tab.contacts'), screen: 'contacts' },
    { id: 'calendar', icon: 'calendar-outline',       label: t('tab.calendar'), screen: 'business-calendar' },
    { id: 'orders',   icon: 'receipt-outline',        label: t('tab.orders'),   screen: 'orders' },
    { id: 'more',     icon: 'ellipsis-horizontal',    label: t('tab.more'),     screen: 'more' },
  ];

  const handleTabPress = (tab: TabItem) => {
    if (tab.id === 'more') {
      // Show more options modal
      setShowMoreModal(true);
    } else {
      // Map calendar tab to business-calendar screen
      const screenId = tab.id === 'calendar' ? 'business-calendar' : tab.id;
      // Use the onTabPress callback for navigation
      onTabPress(screenId);
    }
  };

  const renderTabIcon = (tab: TabItem) => {
    if (tab.id === 'more' && user) {
      // Get profile picture URL from avatar or profilePicture field
      const profilePictureUrl = user.avatar || user.profilePicture;
      const hasValidProfilePicture = !imageError && 
                                     profilePictureUrl && 
                                     typeof profilePictureUrl === 'string' &&
                                     profilePictureUrl.trim() !== '' && 
                                     profilePictureUrl !== 'null' && 
                                     profilePictureUrl !== 'undefined' &&
                                     (profilePictureUrl.startsWith('http://') || 
                                      profilePictureUrl.startsWith('https://') || 
                                      profilePictureUrl.startsWith('file://') || 
                                      profilePictureUrl.startsWith('data:'));
      
      // Get user initials
      const firstName = user.firstName || user.name?.split(' ')[0] || '';
      const lastName = user.lastName || user.name?.split(' ').slice(1).join(' ') || '';
      const firstInitial = firstName ? firstName.charAt(0).toUpperCase() : '';
      const lastInitial = lastName ? lastName.charAt(0).toUpperCase() : '';
      const initials = (firstInitial + lastInitial) || 'U';
      
      // Show profile picture if available and no error, otherwise show initials
      return (
        <View style={styles.userThumbnail}>
          {hasValidProfilePicture ? (
            <>
              {Platform.OS === 'web' ? (
                <img
                  src={profilePictureUrl}
                  alt="Profile"
                  style={{
                    width: 24,
                    height: 24,
                    borderRadius: 12,
                    objectFit: 'cover' as const,
                    display: 'block',
                  }}
                  onError={() => {
                    setImageError(true);
                  }}
                />
              ) : (
                <Image
                  source={{ uri: profilePictureUrl }}
                  style={styles.userThumbnailImage}
                  onError={() => {
                    setImageError(true);
                  }}
                />
              )}
            </>
          ) : (
            <Text style={[styles.userInitials, { fontFamily: typo.fontFamily.semibold }]}>{initials}</Text>
          )}
        </View>
      );
    }
    
    return (
      <Ionicons
        name={tab.icon as any}
        size={22} // Slightly smaller icon for 6 tabs (was 24)
        color={activeTab === tab.id ? colors.primary : colors.textLight}
      />
    );
  };

  return (
    <>
      <View style={[styles.container, { paddingBottom: Platform.OS !== 'web'? insets.bottom : spacing.sm }]}>
        {(rtl.isRTL && Platform.OS !== 'web' ? [...tabs].reverse() : tabs).map((tab) => {
          return (
            <TouchableOpacity
              key={tab.id}
              style={styles.tab}
              onPress={() => {
                handleTabPress(tab);
              }}
              activeOpacity={0.7}
            >
              {renderTabIcon(tab)}
              <Text
                style={[
                  styles.tabLabel,
                  { fontFamily: typo.fontFamily.medium },
                  activeTab === tab.id && styles.activeTabLabel,
                  activeTab === tab.id && { fontFamily: typo.fontFamily.semibold },
                ]}
                numberOfLines={1}
                adjustsFontSizeToFit={true}
                minimumFontScale={0.7}
                ellipsizeMode="tail"
              >
                {tab.label}
              </Text>
            </TouchableOpacity>
          );
        })}
      </View>

      {/* More Options Modal */}
      <MoreOptionsModal
        visible={showMoreModal}
        onClose={() => setShowMoreModal(false)}
      />
    </>
  );
};

const getStyles = () => StyleSheet.create({
  container: {
    flexDirection: 'row',
    backgroundColor: colors.background.surface,
    borderTopWidth: 1,
    borderTopColor: colors.border,
    paddingTop: spacing.sm,
    elevation: 8,
    shadowColor: colors.textDark,
    shadowOffset: { width: 0, height: -2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
  },
  tab: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: spacing.xs,
    paddingHorizontal: Platform.OS === 'web' ? spacing.xs : 2, // Reduce horizontal padding for 6 tabs
    minHeight: 50,
    minWidth: 0, // Allow tabs to shrink below content size
  },
  tabLabel: {
    fontSize: Platform.OS === 'web' ? typography.fontSize.xs : 10,
    color: colors.textLight,
    marginTop: 2,
    textAlign: 'center',
    lineHeight: 14,
  },
  activeTabLabel: {
    color: colors.primary,
    fontWeight: '600',
  },
  userThumbnail: {
    width: 24,
    height: 24,
    borderRadius: 12,
    backgroundColor: colors.primary,
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
  },
  userThumbnailImage: {
    width: 24,
    height: 24,
    borderRadius: 12,
  },
  userInitials: {
    fontSize: typography.fontSize.xs,
    fontWeight: '600',
    color: colors.white,
  },
});
